import React from 'react'
import { Form, Input, TextArea } from 'antd-mobile'
import type { FC } from 'react'
import { DpPicker, DpMultiplePicker, DpCalendarPicker, DpCarNumber, DpPhoneNumber, DpStepper, DpDatePicker, DpUploadV2 } from '@/libs/components'
import { isJSONParseArray, isJSONParse } from './until'
import { validatorPattern } from './validator'
import dayjs from 'dayjs'
import DpPickerSearch from '@/libs/components/DpFunctionComponents/DpPickerSearch'

type PropsType = {
  name: any[string]
  fieldName: string
  label: string
  fieldCode: string
  required: number
  desc: string
  fieldTips: string
  firstIndex: number
  secondIndex: number
  optionValue: any
  value: any
  fieldType?: string
  initialValue: any
  disabled?: boolean
  configType?: string // 配置方式，是否有前缀，默认配置和国际化配置 default、‌i18n‌Config
}


export const FormItem: FC<any> = (props) => {
  const { fields, children, pattern = [],...resProps } = props
  const type = (fields) => {
    switch (fields.fieldType) {
      case 'select-single':
      case 'custom-select-single':
      case 'timePicker-booking':// 预约时间
      case 'select-multiple':
      case 'custom-select-multiple':
      case 'date':
      case 'custom-date':
      case 'carNumber': // 车牌号
        return '请选择'
      case 'upload':
      case 'custom-upload':
        return '请上传'
      default:
        return '请输入'
    }
  }
  return (
    <Form.Item
      name={[...fields.name, 'fieldValue']}
      label={fields?.fieldName}
      rules={[
        { required: fields?.required === 1, message: `${type(fields)}${fields?.fieldName}` },
        ...(pattern || [])
      ]}
      {...resProps}
    >
      {children}
      
    </Form.Item>
  )
}

// 文本域
export const FieldTextarea = (fields: PropsType, extra?: Record<string, any>) => {
  const attributes = isJSONParse(extra?.attributes) || {}
  const { pattern = [], ...resAttributes } = attributes
  return (
    <FormItem fields={fields} pattern={pattern}>
      <TextArea placeholder={fields?.fieldTips} disabled={fields?.disabled || extra?.disabled} {...resAttributes} />
    </FormItem>
  )
}

// 文本
export const FieldText = (fields: PropsType, extra?: Record<string, any>) => {
  const attributes = isJSONParse(extra?.attributes) || {}
  const { pattern = [], min, max, ...resAttributes } = attributes
  const _pattern = pattern || []
  if (max) {
    _pattern.unshift({ max, message: `最大可输入${max}字符` })
  }
  if (min) {
    _pattern.unshift({ min, message: `最小可输入${min}字符` })
  }
  const yetPattern = validatorPattern(fields?.fieldCode, fields?.fieldName)
  if (yetPattern?.length && !_pattern?.find(_ => _.pattern === yetPattern?.[0]?.pattern)) {
    _pattern.unshift(yetPattern?.[0])
  }
  return (
    <FormItem fields={fields} pattern={_pattern}>
      <Input
        placeholder={fields?.fieldTips || '请输入'}
        disabled={fields?.disabled || extra?.disabled}
        {...resAttributes}
      />
    </FormItem>
  )
}

// 数值
export const FieldInteger = (fields: PropsType, extra?: Record<string, any>) => {
  const attributes = isJSONParse(extra?.attributes) || {}
  const { pattern = [], ...resAttributes } = attributes
  return (
    <FormItem fields={fields} pattern={pattern}>
      <DpStepper
        hiddenTitle={true}
        bodyStyle={{ padding: 0 }}
        stepperProps={{
          ...resAttributes,
          defaultValue: null,
          allowEmpty: true,
          digits: resAttributes?.precision || 0,
          style: { width: '100%' },
          placeholder: fields?.fieldTips || '请输入',
          disabled: fields?.disabled || extra?.disabled,
          min: resAttributes?.min || 0,
          max:  resAttributes?.max || 10000
        }}
      />
    </FormItem>
  )
}



// 手机号
export const FieldPhone = (fields: PropsType, extra?: Record<string, any>) => {
  return (
    <Form.Item
      name={[...fields.name, 'fieldValue']}
      label={fields?.fieldName}
      required={fields?.required === 1}
      rules={[{
        validator: (_, value) => {
          const checkRes = DpPhoneNumber.Validator.ValidatorRule({ value, type: fields?.configType || 'default', required: fields?.required === 1, msg: `${lang['请输入']}${fields?.fieldName}` })
          return checkRes?.check ? Promise.resolve() : Promise.reject(checkRes?.msg)
        }
      }]}
    >
      <DpPhoneNumber
        customStyles={extra?.customStyles}
        {...fields}
        disabled={fields?.disabled || extra?.disabled}
        placeholder={fields?.fieldTips || lang['请输入']}
      />
    </Form.Item>
  )
}

// 设置
const setSelectValue = (fields: PropsType, _currentValue: any, extra: any, key?: string) => {
  const allValues = extra?.form?.getFieldsValue()
  let laseGroupDetailList = allValues?.[fields.name[0]]
  laseGroupDetailList[fields.name[1]].fieldList[fields.name[3]].value =
    _currentValue
  extra?.form?.setFieldsValue?.({
    ...allValues,
    [fields.name[0]]: laseGroupDetailList,
  })
}

// 多选
export const FieldSelectMultiple = (fields: PropsType, extra?: Record<string, any>) => {
  const optionValue = isJSONParseArray?.(fields?.optionValue) || []
  return (
    <FormItem
      fields={fields}
      style={{ marginBottom: 0 }}
      normalize={(value?: any[]) => value?.join(',')}
    >
      <DpMultiplePicker
        columns={optionValue}
        multiple={true}
        title={fields?.fieldName || '请选择'}
        placeholder={fields?.fieldTips || '请选择'}
        onChange={(e: string) => {
          const _currentValue = optionValue?.filter((_: Record<string, any>) => e?.includes?.(_?.value))?.map((item: { label?: string }) => item.label)?.join?.(',')
          setSelectValue(fields, _currentValue, extra)
        }}
        disabled={fields?.disabled || extra?.disabled}
        customStyles={extra?.customStyles}
      />
    </FormItem>
  )
}

// 单选
export const FieldSelectSingle = (fields: PropsType, extra?: Record<string, any>) => {
  const optionValue = isJSONParseArray?.(fields?.optionValue) || []
  const attributes = isJSONParse(extra?.attributes) || {}
  return (
    <FormItem fields={fields} style={{ marginBottom: 0 }} normalize={(value?: any[]) => Array.isArray(value) ? value.join(',') : value}>
      {
        attributes?.dataType === 'api' ?
          <DpPickerSearch
            config={attributes?.apiConfig?.find((node: { channel?: string }) => node?.channel === 'bsaas')}
            disabled={fields?.disabled || extra?.disabled}
            customStyles={extra?.customStyles}
            placeholder={fields?.fieldTips || '请选择'}
            onChange={(e: string, item?: Record<string, any>) => {
              const _currentValue = item?.label || optionValue?.find((_: Record<string, any>) => _?.value === e?.[0])?.label
              setSelectValue(fields, _currentValue, extra)
            }}
          />
          :
          <DpPicker
            columns={[optionValue]}
            title={fields?.fieldName || '请选择'}
            placeholder={fields?.fieldTips || '请选择'}
            onChange={(e: string) => {
              const _currentValue = optionValue?.find((_: Record<string, any>) => _?.value === e?.[0])?.label
              setSelectValue(fields, _currentValue, extra)
            }}
            disabled={fields?.disabled || extra?.disabled}
            customStyles={extra?.customStyles}
          />
      }
    </FormItem>
  )
}

// 日期
export const FieldDate = (fields: PropsType, extra?: Record<string, any>) => {
  const attributes = isJSONParse(extra?.attributes) || {}
  const { pattern = [], ...resAttributes } = attributes
  return (
    <FormItem fields={fields} style={{ marginBottom: 0 }}>
      <DpCalendarPicker
        isTheme={extra?.isTheme}
        disabled={fields?.disabled || extra?.disabled}
        placeholder={fields?.fieldTips || '请选择日期'}
        {...resAttributes}
        calendarProps={resAttributes?.type === 'rangePicker' ? {
          selectionMode: 'range',
          type: 'list'
        }: undefined }
        summitChangeValue={(value) => resAttributes?.type === 'rangePicker' ? value?.join?.('~') : value}
        transformChangeValue={(value) => {
          if (resAttributes?.type === 'rangePicker' && typeof value === 'string') {
            let _value: any[] = value?.split?.('~')
            _value = _value?.map(item =>{
              const formattedDateStr = item.replace(/年|月/g, '-')?.replace('日', ''); // 将年、月、日替换为，dayjs需要设置中文，直接粗暴处理
              return dayjs(formattedDateStr, 'YYYY-MM-DD');
            })
            return _value
          }
          return value
        }}
      />
    </FormItem>
  )
}

// // 预约时间
// export const FieldBookingTime = (fields: PropsType, extra?: Record<string, any>) => {
//   return (
//     <FormItem fields={fields} style={{ marginBottom: 0 }}>
//       <DpTimePickerView minuteStep={15} placeholder={fields?.fieldTips || '请选择时间'} />
//     </FormItem>
//   )
// }

// 预约时间
export const FieldBookingTime = (fields: PropsType, extra?: Record<string, any>) => {
  const attributes = isJSONParse(extra?.attributes) || {}
  const { pattern = [], ...resAttributes } = attributes
  const [start, end] = resAttributes?.range?.split('~') || []
  return (
    <FormItem fields={fields} style={{ marginBottom: 0 }}>
      <DpDatePicker
        style={extra?.customStyles}
        {...resAttributes}
        type='time'
        disabledTime={start ? { start, end } : undefined}
        pickerProps={{
          style: extra?.customStyles
        }}
        config={{
          'HH': {
            type: 'hour',
            defaultStep: resAttributes?.hourStep || 1,
            start: 0,
            unit: lang['时']
          },
          'mm': {
            type: 'minute',
            defaultStep: resAttributes?.minuteStep || 1,
            start: 0,
            unit: lang['分']
          },
          'ss': {
            type: 'second',
            defaultStep: resAttributes?.secondStep || 1,
            start: 0,
            unit: lang['秒']
          }
        }}
      />
    </FormItem>
  )
}

// 上传
export const FieldUpload = (fields: PropsType, extra?: Record<string, any>) => {
  const attributes = isJSONParse(extra?.attributes) || {}
  const { pattern = [], ...resAttributes } = attributes
  return (
    <FormItem
      fields={fields}
      style={{ marginBottom: 0 }}
    >
      <DpUploadV2
        explain={fields.desc}
        maxCount={Number(resAttributes?.max || 100)}
        disabled={fields?.disabled}
        {...resAttributes}
      />
    </FormItem>
  )
}

// 身份证
export const FieldCertificate = (fields: PropsType, extra?: Record<string, any>) => {
  const optionValue = isJSONParseArray?.(fields?.optionValue) || []
  const idType = Form.useWatch(
    [...fields.name, 'fieldValue', 'idType'],
    extra?.form,
  )
  return (
    <React.Fragment>
      <Form.Item
        name={[...fields.name, 'fieldValue', 'idType']}
        label={fields?.fieldName}
        rules={[{ required: !!fields?.required, message: `请选择` }]}
        style={{ marginBottom: 0 }}
        normalize={(value?: any) => value?.join?.(',')}
      >
        <DpPicker
          columns={[optionValue]}
          title={fields?.fieldName || '请选择'}
          disabled={fields?.disabled || extra?.disabled}
          placeholder={fields?.fieldTips || '请选择'}
          onChange={(e: string) => {
            const _currentValue = optionValue?.find(
              (_: Record<string, any>) => _?.value === e?.[0],
            )?.label
            const allValues = extra?.form?.getFieldsValue()
            let laseGroupDetailList = allValues?.[fields.name[0]]
            const _currentFieldValue =
              laseGroupDetailList[fields.name[1]].fieldList[fields.name[3]]
                ?.fieldValue
            const currentFieldValue = {
              idType: e?.[0],
              idNumber: _currentFieldValue?.idNumber,
              idTypeName: _currentValue,
            }
            laseGroupDetailList[fields.name[1]].fieldList[
              fields.name[3]
            ].fieldValue = currentFieldValue
            extra?.form?.setFieldsValue?.({
              ...allValues,
              [fields.name[0]]: laseGroupDetailList,
            })
          }}
        />
      </Form.Item>
      <Form.Item name={[...fields.name, 'fieldValue', 'idTypeName']} hidden>
        <Input />
      </Form.Item>
      <Form.Item
        name={[...fields.name, 'fieldValue', 'idNumber']}
        label={'证件号码'}
        rules={[
          { required: fields?.required === 1, message: `请输入证件号码` },
          ...(validatorPattern(idType) || []),
        ]}
        style={{ marginBottom: 0 }}
        dependencies={[...fields.name, 'fieldValue', 'idType']}
      >
        <Input
          placeholder="请输入证件号码"
          disabled={fields?.disabled || extra?.disabled}
        />
      </Form.Item>
    </React.Fragment>
  )
}

// 川FAD1E7
// 车牌号码
export const FieldCarNumber = (fields: PropsType, extra?: Record<string, any>) => {
  return (
    <FormItem
      fields={fields}
      style={{ marginBottom: 0 }}
      required={!!fields?.required}
      rules={[
        {
          validator(rule, value) {
            const _length = value?.filter((_: string) => _)?.length
            if (!!fields?.required && !_length) {
              return Promise.reject(new Error(`请配置${fields?.label || '车牌号码'}`));
            }
            if (value?.filter((_: string) => _)?.length && (!value?.[0] || !value?.[1])) {
              return Promise.reject(new Error(`请配置完整的${fields?.label || '车牌号码'}`));
            }
            const numberArr = value?.[1]?.split('')
            if (_length === 2 && (numberArr.length !== 6 && numberArr.length !== 5)) {
              return Promise.reject(new Error(`请配置正确的${fields?.label || '车牌号码'}`));
            }
            // if (_length === 2 && !/^([京津沪渝冀豫云辽黑湘皖鲁新苏浙赣鄂桂甘晋蒙陕吉闽贵粤青藏川宁琼使领A-Z]{1}[a-zA-Z](([DF]((?![IO])[a-zA-Z0-9](?![IO]))[0-9]{4})|([0-9]{5}[DF]))|[京津沪渝冀豫云辽黑湘皖鲁新苏浙赣鄂桂甘晋蒙陕吉闽贵粤青藏川宁琼使领A-Z]{1}[A-Z]{1}[A-Z0-9]{4}[A-Z0-9挂学警港澳]{1})$/.test(value?.join(""))) {
            //   return Promise.reject(`请配置正确格式的${fields?.label || '车牌号码'}`);
            // }
            return Promise.resolve()
          },
        },
      ]}
    >
      <DpCarNumber disabled={fields?.disabled || extra?.disabled} />
    </FormItem>
  )
}
